import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, AlertCircle, CheckCircle, Shield, ArrowLeft, Monitor, Key, Fingerprint, Eye, EyeOff } from 'lucide-react';
import DateOfBirthModal from '../components/modals/DateOfBirthModal';
import { checkBiometricAvailability, authenticateWithBiometric, getBiometricCredentials, saveBiometricCredentials, isNative } from '../utils/capacitor';
import { useHaptic } from '../hooks/useHaptic';
import { apiClient } from '../api/client';
import { devLog } from '../utils/devLogger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEVICE_TOKEN_KEY = 'hibff_trusted_device';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, socialLogin, completeSocialSignup, setUser } = useAuth();
  const haptic = useHaptic();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDOBModal, setShowDOBModal] = useState(false);
  const [pendingSocialUser, setPendingSocialUser] = useState(null);
  const [showReactivationModal, setShowReactivationModal] = useState(false);
  const [dormantUser, setDormantUser] = useState(null);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('none');

  // 2FA state
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState('sms'); // 'sms' or 'email'
  const [twoFAUserId, setTwoFAUserId] = useState('');
  const [twoFAMaskedPhone, setTwoFAMaskedPhone] = useState('');
  const [twoFAMaskedEmail, setTwoFAMaskedEmail] = useState('');
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resendSending, setResendSending] = useState(false);
  const [resendNotice, setResendNotice] = useState('');

  // Countdown for the resend-code cooldown
  useEffect(() => {
    if (!resendSeconds) return undefined;
    const id = setInterval(() => setResendSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendSeconds]);
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', '']);
  const [twoFAVerifying, setTwoFAVerifying] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [showRecoveryInput, setShowRecoveryInput] = useState(false);
  const [emailFallbackSending, setEmailFallbackSending] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const digitRefs = useRef([]);

  // Persona selector — restores last choice or defaults to 'teen'
  const [persona, setPersona] = useState(() => {
    try { return localStorage.getItem('hibff_login_persona') || 'teen'; }
    catch (e) { return 'teen'; }
  });
  useEffect(() => {
    try { localStorage.setItem('hibff_login_persona', persona); } catch (e) { /* */ }
  }, [persona]);
  const handlePersonaPick = (next) => {
    if (next === 'parent') {
      navigate('/parent-login');
      return;
    }
    setPersona(next);
  };
  const PERSONA_THEME = {
    teen:  { grad: 'from-indigo-600 to-violet-600',  glow: 'shadow-indigo-200',  sub: 'Sign back into your practice space — your AvA is waiting.' },
    adult: { grad: 'from-amber-500 to-orange-600',   glow: 'shadow-amber-200',   sub: 'Sign back into your journey — pick up where you left off.' },
  };
  const theme = PERSONA_THEME[persona] || PERSONA_THEME.teen;

  useEffect(() => {
    if (location.state?.message) {
      if (location.state?.tone === 'error') {
        setError(location.state.message);
      } else {
        setSuccessMessage(location.state.message);
      }
    }
  }, [location]);

  // Check biometric availability on native platforms
  useEffect(() => {
    checkBiometricAvailability().then(({ available, type }) => {
      setBiometricAvailable(available);
      setBiometricType(type);
    });
  }, []);

  const handleBiometricLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const verified = await authenticateWithBiometric('Log in to HiBFF');
      if (!verified) { setLoading(false); return; }

      const creds = await getBiometricCredentials('hibff.com');
      if (!creds) {
        setError('No saved credentials. Please log in with email first, then enable biometric.');
        setLoading(false);
        return;
      }

      const userData = await login(creds.username, creds.password, true);
      haptic.success();
      routeUser(userData);
    } catch (err) {
      setError('Biometric login failed. Please use email and password.');
      haptic.error();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      // First, make a direct API call to check for 2FA
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      const data = await apiClient.request('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          device_token: deviceToken || null,
        }),
      }).catch(err => {
        // apiClient.request throws on non-2xx; normalise to the old shape
        throw new Error(err.message || 'Login failed');
      });

      // Check if 2FA is required
      if (data.two_fa_required) {
        setTwoFARequired(true);
        setTwoFAUserId(data.user_id);
        setTwoFAMethod(data.two_fa_method || 'sms');
        setTwoFAMaskedPhone(data.masked_phone || '');
        setTwoFAMaskedEmail(data.masked_email || '');
        setTwoFACode(['', '', '', '', '', '']);
        setTwoFAError('');
        setResendNotice('');
        setResendSeconds(30);
        setLoading(false);
        // Focus first digit input after render
        setTimeout(() => digitRefs.current[0]?.focus(), 100);
        return;
      }

      // No 2FA — complete login normally via AuthContext
      const userData = await login(formData.email, formData.password, formData.rememberMe);
      routeUser(userData);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Invalid email or password') || msg === 'WRONG_PASSWORD') {
        setError('Incorrect email or password. Please try again.');
      } else if (msg === 'NOT_VERIFIED') {
        setError('Please verify your email before logging in.');
      } else if (msg === 'AWAITING_PARENT') {
        setError('Your account is awaiting parent setup.');
        navigate('/waiting-for-parent', { state: { parentEmail: 'parent email' } });
      } else {
        setError(msg || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEmailCode = async () => {
    setEmailFallbackSending(true);
    setTwoFAError('');
    try {
      const res = await apiClient.request('/api/auth/2fa/send-email-code', {
        method: 'POST',
        body: JSON.stringify({ user_id: twoFAUserId }),
      });
      setTwoFAMethod('email');
      setTwoFAMaskedEmail(res.masked_email || '');
      setTwoFACode(['', '', '', '', '', '']);
      setResendSeconds(30);
    } catch (err) {
      setTwoFAError(err.message || 'Could not send the email code. Please try again.');
    } finally {
      setEmailFallbackSending(false);
    }
  };

  // Resend the pending code over its original channel (SMS or email) —
  // safety net for carrier-dropped SMS. Backend enforces 30s cooldown + cap.
  const handleResendCode = async () => {
    if (resendSending || resendSeconds > 0) return;
    setResendSending(true);
    setTwoFAError('');
    setResendNotice('');
    try {
      const res = await apiClient.request('/api/auth/2fa/resend-code', {
        method: 'POST',
        body: JSON.stringify({ user_id: twoFAUserId }),
      });
      setResendNotice(res.message || 'A new code has been sent');
      setResendSeconds(res.cooldown_seconds || 30);
      setTwoFACode(['', '', '', '', '', '']);
      setTimeout(() => digitRefs.current[0]?.focus(), 100);
    } catch (err) {
      setTwoFAError(err.message || 'Could not resend the code. Please try again.');
    } finally {
      setResendSending(false);
    }
  };

  // Accepts an optional code string so auto-submit/paste paths can pass the
  // freshly-typed code directly — React state hasn't committed yet when the
  // 6th digit triggers submit (fixes the false "Please enter all 6 digits").
  const handleVerify2FA = async (codeOverride) => {
    const code = typeof codeOverride === 'string' ? codeOverride : twoFACode.join('');
    if (code.length !== 6) {
      setTwoFAError('Please enter all 6 digits');
      return;
    }

    setTwoFAVerifying(true);
    setTwoFAError('');

    try {
      const data = await apiClient.request('/api/auth/verify-2fa', {
        method: 'POST',
        body: JSON.stringify({
          user_id: twoFAUserId,
          code,
          trust_device: trustDevice,
        }),
      }).catch(err => {
        setTwoFAError(err.message || 'Invalid code');
        setTwoFAVerifying(false);
        throw err;
      });

      // Store trusted device token if returned
      if (data.device_token) {
        localStorage.setItem(DEVICE_TOKEN_KEY, data.device_token);
      }

      // 2FA successful — complete login via AuthContext
      const userData = await login(formData.email, formData.password, formData.rememberMe);
      routeUser(userData);
    } catch (err) {
      // Error was already handled via setTwoFAError in the inner catch
      if (!twoFAError) setTwoFAError(err.message || 'Verification failed. Please try again.');
      setTwoFAVerifying(false);
    }
  };

  const handleVerifyRecoveryCode = async () => {
    const cleaned = recoveryCode.trim().toUpperCase();
    if (!cleaned || cleaned.length < 4) {
      setTwoFAError('Please enter a valid recovery code');
      return;
    }

    setTwoFAVerifying(true);
    setTwoFAError('');

    try {
      const data = await apiClient.request('/api/auth/verify-2fa-recovery', {
        method: 'POST',
        body: JSON.stringify({
          user_id: twoFAUserId,
          recovery_code: cleaned,
          trust_device: trustDevice,
        }),
      }).catch(err => {
        setTwoFAError(err.message || 'Invalid recovery code');
        setTwoFAVerifying(false);
        throw err;
      });

      // Store trusted device token if returned
      if (data.device_token) {
        localStorage.setItem(DEVICE_TOKEN_KEY, data.device_token);
      }

      // Show warning about remaining codes
      if (data.warning) {
        console.warn(data.warning);
      }

      // Recovery code verified — complete login
      const userData = await login(formData.email, formData.password, formData.rememberMe);
      routeUser(userData);
    } catch (err) {
      setTwoFAError(err.message || 'Recovery failed. Please try again.');
      setTwoFAVerifying(false);
    }
  };

  const handle2FADigitChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...twoFACode];
    newCode[index] = value;
    setTwoFACode(newCode);
    if (value) setTwoFAError('');

    // Auto-advance to next input
    if (value && index < 5) {
      digitRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered — pass the code directly since
    // state won't have committed yet inside the timeout closure.
    if (value && index === 5 && newCode.every(d => d !== '')) {
      setTimeout(() => handleVerify2FA(newCode.join('')), 100);
    }
  };

  const handle2FAKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      handleVerify2FA();
    }
  };

  const handle2FAPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const digits = pasted.split('');
      setTwoFACode(digits);
      digitRefs.current[5]?.focus();
      setTimeout(() => handleVerify2FA(pasted), 100);
    }
  };

  const routeUser = (userData) => {
    // Save credentials for biometric login on native platforms
    if (biometricAvailable && formData.email && formData.password) {
      saveBiometricCredentials('hibff.com', formData.email, formData.password);
    }
    haptic.success();

    // Deep-link handoff — if the user was bounced here from a protected page
    // (e.g. an admin email's "Review & Approve" link), return them to it with
    // the query string intact instead of the generic role landing page.
    const from = location.state?.from;
    if (from?.pathname) {
      navigate(`${from.pathname}${from.search || ''}`, { replace: true });
      return;
    }

    if (userData?.role === 'admin' || userData?.role === 'sub_admin') {
      navigate('/admin');
    } else if (userData?.role === 'developer') {
      navigate('/developer');
    } else if (userData?.role === 'school_admin' || userData?.role === 'teacher') {
      navigate('/school');
    } else if (userData?.role === 'corporate_admin' || userData?.role === 'manager') {
      navigate('/corporate');
    } else if (userData?.role === 'parent') {
      navigate('/parent-dashboard');
    } else if (userData?.school_id) {
      navigate('/school/student');
    } else if (userData?.org_id) {
      navigate('/corporate/member');
    } else {
      navigate('/dashboard');
    }
  };

  const handleReactivate = async () => {
    try {
      // Update user to remove dormant status
      const users = JSON.parse(localStorage.getItem('hibff_users') || '[]');
      const userIndex = users.findIndex(u => u.email === dormantUser.email);

      if (userIndex !== -1) {
        users[userIndex].accountStatus = 'active';
        users[userIndex].subscriptionPaused = false;
        delete users[userIndex].deactivatedDate;
        delete users[userIndex].reactivationDeadline;
        localStorage.setItem('hibff_users', JSON.stringify(users));
      }

      // Log them in
      await login(formData.email, formData.password, formData.rememberMe);

      devLog(`📧 [mock] Reactivation email queued for ${dormantUser.email}`);

      navigate('/');
    } catch (err) {
      setError('Failed to reactivate account. Please try again.');
    }
  };

  const [googleWaiting, setGoogleWaiting] = useState(false);

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      // Direct Google OAuth — no Emergent intermediate screen.
      // In the native app the flow finishes in the system browser and we
      // poll for the result (device-link); on the web the page navigates away.
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const { signInWithGoogle, routeForUser, isNativeApp } = await import('../utils/googleAuth');
      if (isNativeApp()) setGoogleWaiting(true);
      const data = await signInWithGoogle();
      if (data) {
        localStorage.setItem('hibff_auth_token', data.access_token);
        sessionStorage.removeItem('hibff_auth_token');
        localStorage.setItem('hibff_user_cache', JSON.stringify(data.user));
        if (setUser) setUser(data.user);
        navigate(routeForUser(data.user), { replace: true });
      }
    } catch (err) {
      if (err.message !== 'GOOGLE_SIGNIN_CANCELLED') {
        setError(err.message || 'Google sign-in failed. Please try again.');
      }
      setLoading(false);
    } finally {
      setGoogleWaiting(false);
    }
  };

  const cancelGoogleWait = async () => {
    const { cancelGoogleSignIn } = await import('../utils/googleAuth');
    cancelGoogleSignIn();
  };

  const handleFacebookLogin = async () => {
    setError('');
    setLoading(true);

    try {
      // Mock Facebook OAuth user (sometimes provides DOB)
      const mockFBUser = {
        name: 'Emma Davis',
        email: 'emma.davis@facebook.com',
        profilePicture: 'https://api.dicebear.com/7.x/avataaars/svg?seed=EmmaFB&backgroundColor=ffd5dc',
        authProvider: 'facebook'
      };

      const result = await socialLogin('Facebook', mockFBUser, formData.rememberMe);

      if (result.type === 'LOGIN_SUCCESS') {
        // Existing user - logged in
        navigate('/');
      } else if (result.type === 'NEEDS_DOB') {
        // New user - need DOB
        setPendingSocialUser(result.userData);
        setShowDOBModal(true);
      }
    } catch (err) {
      setError('Facebook sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDOBSubmit = async ({ dob, age }) => {
    setShowDOBModal(false);
    setLoading(true);

    try {
      if (age < 13) {
        setError('You must be at least 13 years old to use HiBFF. For more information, visit our COPPA Compliance page.');
        return;
      }

      const result = await completeSocialSignup(pendingSocialUser, dob, age, formData.rememberMe);

      if (result.type === 'SIGNUP_SUCCESS') {
        // Adult user - signup complete
        navigate('/');
      } else if (result.type === 'NEEDS_PARENT_CONSENT') {
        // Teen user - needs parent consent
        navigate('/register', {
          state: {
            socialUser: result.userData,
            step: 2 // Go directly to parent consent step
          }
        });
      }
    } catch (err) {
      setError('Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-6">
          <img src="/hibff-logo.png" alt="HiBFF" className="h-44 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">Welcome Back</h1>
          <p className="text-gray-600 mt-2" data-testid="login-subhead">
            {theme.sub}
          </p>
        </div>

        {/* Persona Selector — only show on the email-form view, not on the 2FA gate */}
        {!twoFARequired && (
          <div
            className="grid grid-cols-3 gap-2 mb-6 p-1.5 rounded-2xl bg-white/70 backdrop-blur-sm border border-gray-200 shadow-sm"
            data-testid="login-persona-selector"
            role="tablist"
          >
            {[
              { id: 'teen',   label: 'Teen',   emoji: '\u{1F393}', age: '13–19' },
              { id: 'adult',  label: 'Adult',  emoji: '\u{1F31F}', age: '20+' },
              { id: 'parent', label: 'Parent', emoji: '\u{1F46A}', age: 'GUARDIAN'  },
            ].map((p) => {
              const active = p.id === persona && p.id !== 'parent';
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handlePersonaPick(p.id)}
                  data-testid={`persona-pill-${p.id}`}
                  role="tab"
                  aria-selected={active}
                  className={`flex flex-col items-center justify-center gap-0.5 py-2.5 rounded-xl text-sm font-bold transition-all ${
                    active
                      ? `bg-gradient-to-r ${theme.grad} text-white shadow-md ${theme.glow}`
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="text-lg leading-none">{p.emoji}</span>
                  <span className="leading-none">{p.label}</span>
                  <span className={`text-[9px] font-semibold tracking-widest uppercase ${active ? 'text-white/80' : 'text-gray-400'}`}>
                    {p.age}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* 2FA Verification Screen */}
        {twoFARequired ? (
          <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="two-fa-screen">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)' }}>
                <Shield size={28} className="text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Two-Step Verification</h2>
              <p className="text-sm text-gray-500">
                {showRecoveryInput
                  ? 'Enter one of your recovery codes'
                  : twoFAMethod === 'email'
                    ? <>A 6-digit code has been sent to <strong>{twoFAMaskedEmail}</strong></>
                    : <>A 6-digit code has been sent to <strong>{twoFAMaskedPhone}</strong></>
                }
              </p>
            </div>

            {twoFAError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{twoFAError}</p>
              </div>
            )}

            {showRecoveryInput ? (
              <>
                {/* Recovery code input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Recovery Code</label>
                  <div className="relative">
                    <Key size={18} className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      value={recoveryCode}
                      onChange={(e) => { setRecoveryCode(e.target.value.toUpperCase()); setTwoFAError(''); }}
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-500 font-mono tracking-wider text-lg"
                      placeholder="XXXX-XXXX"
                      maxLength={9}
                      data-testid="recovery-code-input"
                      disabled={twoFAVerifying}
                      onKeyDown={(e) => e.key === 'Enter' && handleVerifyRecoveryCode()}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Each code can only be used once</p>
                </div>

                <button
                  onClick={handleVerifyRecoveryCode}
                  disabled={twoFAVerifying || !recoveryCode.trim()}
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                  data-testid="recovery-verify-btn"
                >
                  {twoFAVerifying ? 'Verifying...' : 'Verify Recovery Code'}
                </button>

                <button
                  onClick={() => { setShowRecoveryInput(false); setTwoFAError(''); setRecoveryCode(''); }}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-purple-600 hover:text-purple-700 transition-colors mb-2"
                  data-testid="back-to-sms-btn"
                >
                  <ArrowLeft size={14} />
                  Back to SMS code
                </button>
              </>
            ) : (
              <>
                {/* 6-digit code input */}
                <div className="flex justify-center gap-3 mb-6" onPaste={handle2FAPaste} data-testid="two-fa-inputs">
                  {twoFACode.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => digitRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handle2FADigitChange(i, e.target.value)}
                      onKeyDown={(e) => handle2FAKeyDown(i, e)}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-500 transition-all"
                      style={{ borderColor: digit ? '#7B4FD8' : '#D1D5DB' }}
                      data-testid={`two-fa-digit-${i}`}
                      disabled={twoFAVerifying}
                    />
                  ))}
                </div>

                <button
                  onClick={handleVerify2FA}
                  disabled={twoFAVerifying || twoFACode.some(d => d === '')}
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                  data-testid="two-fa-verify-btn"
                >
                  {twoFAVerifying ? 'Verifying...' : 'Verify & Sign In'}
                </button>

                {/* Resend code — safety net for carrier-dropped SMS/email */}
                {resendNotice && (
                  <p className="text-xs text-center text-green-600 mb-2" data-testid="twofa-resend-notice">{resendNotice}</p>
                )}
                <button
                  onClick={handleResendCode}
                  disabled={resendSending || resendSeconds > 0}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors mb-2 disabled:opacity-50 disabled:cursor-default"
                  data-testid="twofa-resend-btn"
                >
                  {resendSending
                    ? 'Sending…'
                    : resendSeconds > 0
                      ? `Resend code in ${resendSeconds}s`
                      : "Didn't receive it? Resend code"}
                </button>

                {/* Use recovery code link */}
                <button
                  onClick={() => { setShowRecoveryInput(true); setTwoFAError(''); }}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors mb-2"
                  data-testid="use-recovery-code-btn"
                >
                  <Key size={14} />
                  Use a recovery code instead
                </button>

                {/* Email fallback — SMS not arriving */}
                {twoFAMethod === 'sms' && (
                  <button
                    onClick={handleRequestEmailCode}
                    disabled={emailFallbackSending}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-500 hover:text-purple-700 font-medium transition-colors mb-2 disabled:opacity-50"
                    data-testid="twofa-email-fallback-btn"
                  >
                    {emailFallbackSending ? 'Sending email…' : "Didn't get the text? Email me the code instead"}
                  </button>
                )}
              </>
            )}

            {/* Trust this device checkbox */}
            <label className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors mb-4" data-testid="trust-device-label">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                data-testid="trust-device-checkbox"
              />
              <div className="flex items-center gap-2">
                <Monitor size={16} className="text-gray-500" />
                <span className="text-sm text-gray-700">Trust this device for 30 days</span>
              </div>
            </label>

            <button
              onClick={() => { setTwoFARequired(false); setTwoFAError(''); setShowRecoveryInput(false); setRecoveryCode(''); setTrustDevice(false); }}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              data-testid="two-fa-back-btn"
            >
              <ArrowLeft size={14} />
              Back to login
            </button>

            <p className="text-xs text-center text-gray-400 mt-4">
              Code expires in {twoFAMethod === 'email' ? '10' : '5'} minutes. Didn&apos;t receive it? Go back and try again.
            </p>
          </div>
        ) : (
        <>
        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Social Login Buttons */}
          <div className="space-y-3 mb-6">
            {/* Google Login */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Native device-link wait state — retry/cancel (QA suggestion) */}
            {googleWaiting && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-1.5" data-testid="google-signin-waiting-panel">
                <p className="text-sm font-semibold text-blue-900">Finishing sign-in in your browser…</p>
                <p className="text-xs text-blue-800">Approve HiBFF with Google in the browser that just opened, then come back here — we'll sign you in automatically.</p>
                <button type="button" onClick={cancelGoogleWait} className="text-xs font-semibold underline text-blue-700" data-testid="google-signin-cancel-btn">
                  Cancel and try again
                </button>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-3 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-11 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-password-toggle-btn"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-gray-400 hover:text-gray-600 min-w-[40px] min-h-[40px] flex items-center justify-center"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="flex items-center cursor-pointer py-2.5 pr-2 min-h-[44px]" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  data-testid="remember-me-checkbox"
                />
                <span className="ml-2 text-sm text-gray-700">Remember me (30 days)</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-blue-600 hover:text-blue-700 inline-flex items-center min-h-[40px] px-2"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            {/* Biometric Login (native only) */}
            {biometricAvailable && (
              <button
                type="button"
                onClick={handleBiometricLogin}
                disabled={loading}
                className="w-full mt-3 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="biometric-login-button"
              >
                <Fingerprint size={20} className="text-purple-600" />
                {biometricType === 'faceID' ? 'Sign in with Face ID' :
                 biometricType === 'touchID' ? 'Sign in with Touch ID' :
                 'Sign in with Biometric'}
              </button>
            )}
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <a href="/register" className="font-medium text-blue-600 hover:text-blue-700 inline-flex items-center justify-center min-h-[40px] px-2" data-testid="login-signup-link">
                Sign Up
              </a>
            </p>
            </div>
        </div>
        </>
        )}

        {/* Date of Birth Modal (for social login) */}
        <DateOfBirthModal
          isOpen={showDOBModal}
          onClose={() => {
            setShowDOBModal(false);
            setPendingSocialUser(null);
          }}
          onSubmit={handleDOBSubmit}
          userName={pendingSocialUser?.name || 'there'}
        />

        {/* Reactivation Modal (for dormant accounts) */}
        {showReactivationModal && dormantUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                  <CheckCircle size={48} className="text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back!</h2>
                <p className="text-gray-600">
                  Hi {dormantUser.name}! Your account was deactivated on {new Date(dormantUser.deactivatedDate).toLocaleDateString()}.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-green-900 mb-2">Good news: All your data is still here!</p>
                <p className="text-sm text-green-800">
                  You have <strong>{dormantUser.daysRemaining} days remaining</strong> in your reactivation window.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-semibold text-blue-900 mb-2">What happens if you reactivate:</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-sm text-blue-800">
                    <CheckCircle size={16} className="flex-shrink-0 mt-0.5 text-green-600" />
                    <span>All AvAs, conversations, and progress restored</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-blue-800">
                    <CheckCircle size={16} className="flex-shrink-0 mt-0.5 text-green-600" />
                    <span>Subscription resumes (charged from today)</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-blue-800">
                    <CheckCircle size={16} className="flex-shrink-0 mt-0.5 text-green-600" />
                    <span>Pick up exactly where you left off</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowReactivationModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50"
                >
                  Stay Deactivated
                </button>
                <button
                  onClick={handleReactivate}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg"
                >
                  Reactivate My Account
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
